import { useState, useMemo, useCallback } from 'react'
import { processChartData } from './utils'
import { getChartOptions, getChartDataConfig } from './chartConfig'
import { EnhancementReportStat } from '../../../../models/enhancement/enhancement-report-stats'
import { ChartWrapper, useChart, ChartViewLevelSelector } from '../common'
import { EnhancementViewLevel } from '../../ReportViewLevelSelector'

interface HoursVsCallOutRatioChartProps {
  data: EnhancementReportStat[]
  isLoading: boolean
  selectedRequestTypes?: string[] | null
}

function HoursVsCallOutRatioChart({
  data,
  isLoading,
  selectedRequestTypes
}: HoursVsCallOutRatioChartProps) {
  const chartRef = useChart()
  
  const STORAGE_KEY = 'enhancementChart_hoursVsCallOut_viewLevel'
  
  const [viewLevel, setViewLevel] = useState<EnhancementViewLevel>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved && Object.values(EnhancementViewLevel).includes(saved as EnhancementViewLevel)) {
        return saved as EnhancementViewLevel
      }
    } catch (error) {
      console.warn('Failed to load view level:', error)
    }
    return EnhancementViewLevel.DEPARTMENT
  })
  
  const handleViewLevelChange = useCallback((newLevel: EnhancementViewLevel) => {
    setViewLevel(prevLevel => {
      try {
        localStorage.setItem(STORAGE_KEY, newLevel)
      } catch (error) {
        console.warn('Failed to save view level:', error)
      }
      return newLevel
    })
  }, [])
  
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return { departments: [], approvedOnCallHours: [], callOutToOnCallRatios: [] }
    }
    try {
      return processChartData(data, selectedRequestTypes, viewLevel)
    } catch (error) {
      console.error('Error processing chart data:', error)
      return { departments: [], approvedOnCallHours: [], callOutToOnCallRatios: [] }
    }
  }, [data, selectedRequestTypes, viewLevel])

  const chartOptions = useMemo(() => getChartOptions(viewLevel), [viewLevel])
  
  const chartDataConfig = useMemo(() =>
    getChartDataConfig(
      processedData.departments,
      processedData.approvedOnCallHours,
      processedData.callOutToOnCallRatios
    ),
    [processedData]
  )
  
  const hasRequiredRequestTypes = useMemo(() => {
    if (!selectedRequestTypes || selectedRequestTypes.length === 0) {
      return true
    }
    return selectedRequestTypes.includes('On Call') && selectedRequestTypes.includes('Call Out')
  }, [selectedRequestTypes])

  const hasData = Boolean(data?.length && hasRequiredRequestTypes)
  const emptyStateMessage = !hasRequiredRequestTypes
    ? 'This chart requires "On Call" and "Call Out" request types to be selected'
    : 'No data available for the selected filters'

  const chartTitle = `Approved On Call Hours vs Call Out Ratio by ${viewLevel}`
  
  const headerAction = useMemo(() => (
    <ChartViewLevelSelector
      value={viewLevel}
      onViewLevelChange={handleViewLevelChange}
      disabled={isLoading}
      dataTestId="HoursVsCallOut-ViewLevel"
    />
  ), [viewLevel, handleViewLevelChange, isLoading])

  return (
    <ChartWrapper
      ref={chartRef}
      title={chartTitle}
      error={null}
      isLoading={isLoading}
      hasData={hasData}
      emptyStateMessage={emptyStateMessage}
      chartType="bar"
      chartData={chartDataConfig}
      chartOptions={chartOptions}
      height={500}
      headerAction={headerAction}
    />
  )
}

export default HoursVsCallOutRatioChart